import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "../../utils";
import { FileTree, getFirstFilePath } from "../FileTree";
import type { SkillMarkdownPreviewCacheEntry } from "../SkillMarkdownPreview";
import { formatSkillPathForDisplay } from "../utils";

import { OpenaiYamlHeaderToggleButton } from "./OpenaiYamlHeaderToggleButton";
import { isOpenaiYamlFilePath } from "./openai-yaml-path";
import { OpenaiYamlPreviewMode } from "./openai-yaml-preview-mode";
import { SkillCollectionContentHeader } from "./SkillCollectionContentHeader";
import { SkillCollectionViewerPreviewBody } from "./SkillCollectionViewerPreviewBody";
import { useOpenaiYamlPreviewModeState } from "./useOpenaiYamlPreviewModeState";

export interface SkillCollectionViewerProps {
  filePaths: string[];
  files: Record<string, string>;
  initialSelectedPath?: string | null;
  className?: string;
  autoSelectFirstFile?: boolean;
  formatPathForDisplay?: (path: string) => string;
  onSelectedPathChange?: (path: string | null) => void;
  sidebarFooter?: ReactNode;
  renderHeaderTitle?: (
    context: SkillCollectionViewerRenderContext,
  ) => ReactNode;
  renderHeaderActions?: (
    context: SkillCollectionViewerRenderContext,
  ) => ReactNode;
  renderPreview?: (
    context: SkillCollectionViewerRenderContext,
  ) => ReactNode | undefined;
  openaiYamlContentVersionKey?: string | number | null;
  markdownPreviewByPath?: ReadonlyMap<string, SkillMarkdownPreviewCacheEntry>;
  licenseKey?: string;
}

export interface SkillCollectionViewerRenderContext {
  filePaths: string[];
  files: Record<string, string>;
  selectedPath: string | null;
  selectedContent: string;
  isMarkdown: boolean;
  setSelectedPath: (path: string) => void;
  openaiYamlPreviewMode: OpenaiYamlPreviewMode | null;
  setOpenaiYamlPreviewMode: (mode: OpenaiYamlPreviewMode) => void;
  toggleOpenaiYamlPreviewMode: () => void;
}

export const SkillCollectionViewer = ({
  filePaths,
  files,
  initialSelectedPath,
  className,
  autoSelectFirstFile = false,
  formatPathForDisplay = formatSkillPathForDisplay,
  onSelectedPathChange,
  sidebarFooter,
  renderHeaderTitle,
  renderHeaderActions,
  renderPreview,
  openaiYamlContentVersionKey,
  markdownPreviewByPath,
  licenseKey,
}: SkillCollectionViewerProps) => {
  const firstFilePath = useMemo(() => getFirstFilePath(filePaths), [filePaths]);
  const validFilePathSet = useMemo(
    () => new Set(filePaths.filter((path) => !path.endsWith("/"))),
    [filePaths],
  );
  const [selectedPath, setSelectedPath] = useState<string | null>(
    initialSelectedPath ?? (autoSelectFirstFile ? firstFilePath : null),
  );

  const initialSelectedPathRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (initialSelectedPathRef.current === undefined) {
      initialSelectedPathRef.current = initialSelectedPath ?? null;
      return;
    }

    const previous = initialSelectedPathRef.current;
    const next = initialSelectedPath ?? null;
    if (previous === next) {
      return;
    }

    initialSelectedPathRef.current = next;

    if (next === null) {
      setSelectedPath(null);
      return;
    }

    if (validFilePathSet.has(next) && files[next] !== undefined) {
      setSelectedPath(next);
    }
  }, [files, initialSelectedPath, validFilePathSet]);

  const activeSelectedPath = useMemo(() => {
    if (
      selectedPath &&
      validFilePathSet.has(selectedPath) &&
      files[selectedPath] !== undefined
    ) {
      return selectedPath;
    }

    if (
      initialSelectedPath &&
      validFilePathSet.has(initialSelectedPath) &&
      files[initialSelectedPath] !== undefined
    ) {
      return initialSelectedPath;
    }

    return autoSelectFirstFile ? (firstFilePath ?? null) : null;
  }, [
    autoSelectFirstFile,
    files,
    firstFilePath,
    initialSelectedPath,
    selectedPath,
    validFilePathSet,
  ]);

  const {
    openaiYamlPreviewMode,
    setOpenaiYamlPreviewMode,
    toggleOpenaiYamlPreviewMode,
  } = useOpenaiYamlPreviewModeState(activeSelectedPath);

  useEffect(() => {
    onSelectedPathChange?.(activeSelectedPath);
  }, [activeSelectedPath, onSelectedPathChange]);

  const selectedContent = activeSelectedPath
    ? (files[activeSelectedPath] ?? "")
    : "";
  const isMarkdown = activeSelectedPath?.toLowerCase().endsWith(".md") ?? false;
  const isOpenaiYamlSelected = Boolean(
    activeSelectedPath && isOpenaiYamlFilePath(activeSelectedPath),
  );

  const viewerContext: SkillCollectionViewerRenderContext = {
    filePaths,
    files,
    selectedPath: activeSelectedPath,
    selectedContent,
    isMarkdown,
    setSelectedPath,
    openaiYamlPreviewMode: isOpenaiYamlSelected ? openaiYamlPreviewMode : null,
    setOpenaiYamlPreviewMode,
    toggleOpenaiYamlPreviewMode,
  };

  const customPreview = renderPreview?.(viewerContext);
  const headerTitle = renderHeaderTitle?.(viewerContext);
  const headerActionsFromProp = renderHeaderActions?.(viewerContext);

  const showBuiltinOpenaiYamlToggle =
    isOpenaiYamlSelected &&
    renderHeaderTitle === undefined &&
    renderHeaderActions === undefined;

  const effectiveHeaderActions = showBuiltinOpenaiYamlToggle ? (
    <OpenaiYamlHeaderToggleButton
      mode={openaiYamlPreviewMode}
      onToggle={toggleOpenaiYamlPreviewMode}
    />
  ) : (
    headerActionsFromProp
  );
  const headerTrailingContent =
    effectiveHeaderActions === undefined ||
    effectiveHeaderActions === null ? null : (
      <>{effectiveHeaderActions}</>
    );

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-950/5",
        className,
      )}
    >
      <div className="flex w-[276px] shrink-0 flex-col border-r border-gray-200 bg-white">
        <FileTree
          readOnly
          filePaths={filePaths}
          selectedPath={activeSelectedPath}
          onSelectFile={(path) => setSelectedPath(path)}
        />
        {sidebarFooter ? (
          <div className="border-t border-gray-200 px-3 py-2">
            {sidebarFooter}
          </div>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {headerTitle === undefined || headerTitle === null ? (
          <SkillCollectionContentHeader
            filePath={activeSelectedPath}
            displayLabel={
              activeSelectedPath
                ? formatPathForDisplay(activeSelectedPath)
                : undefined
            }
            trailingContent={headerTrailingContent}
          />
        ) : (
          <div className="flex h-[37px] items-center gap-3 border-b border-gray-200 bg-gray-50/70 px-3">
            <div className="min-w-0 flex-1">{headerTitle}</div>
            {effectiveHeaderActions}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto">
          <SkillCollectionViewerPreviewBody
            customPreview={customPreview}
            filePathsLength={filePaths.length}
            activeSelectedPath={activeSelectedPath}
            selectedContent={selectedContent}
            isMarkdown={isMarkdown}
            validFilePathSet={validFilePathSet}
            files={files}
            markdownPreviewByPath={markdownPreviewByPath}
            setSelectedPath={(path) => setSelectedPath(path)}
            openaiYamlPreviewMode={openaiYamlPreviewMode}
            openaiYamlContentVersionKey={openaiYamlContentVersionKey}
            licenseKey={licenseKey}
          />
        </div>
      </div>
    </div>
  );
};

export { SkillCollectionContentHeader } from "./SkillCollectionContentHeader";
export type { SkillCollectionContentHeaderProps } from "./SkillCollectionContentHeader";
export { OpenaiYamlPreviewMode } from "./openai-yaml-preview-mode";
export { isOpenaiYamlFilePath } from "./openai-yaml-path";
